import { Button } from '@/components/ui/button'
import { Artwork, ArtworkStatus, UserRole } from '@/types/entities'
import { useAuth } from '@/features/auth/hooks/useAuth'
import { useUpdateArtwork, usePublishArtwork, useUnpublishArtwork } from '../hooks/useArtworks'
import { useActiveTasks } from '@/features/tasks/hooks/useTasks'
import { useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { CheckCircle, XCircle, Send, ExternalLink } from 'lucide-react'

interface StatusActionsProps {
  artwork: Artwork
}

export function StatusActions({ artwork }: StatusActionsProps) {
  const { user } = useAuth()
  const { mutate: updateStatus } = useUpdateArtwork()
  const { mutate: publish } = usePublishArtwork()
  const { mutate: unpublish } = useUnpublishArtwork()
  const navigate = useNavigate()

  const isManager = user?.role === UserRole.MANAGER
  
  // Only fetch tasks for managers (task API is manager-only)
  const { data: activeTasks } = useActiveTasks(50, 0, isManager)
  
  const publishTask = useMemo(() => {
    if (!isManager) return undefined
    return activeTasks?.data?.find(
      (task) => task.artworkId === artwork.id && task.type === 'PUBLISH_ARTWORK'
    )
  }, [activeTasks?.data, artwork.id, isManager])

  if (!user) return null

  const isArtist = user.role === UserRole.ARTIST

  const handleOpenTask = () => {
    if (!publishTask) return
    navigate(`/app/tasks?taskId=${publishTask.id}`)
  }

  // DRAFT status - Artist can request publication
  if (artwork.status === ArtworkStatus.DRAFT && isArtist) {
    return (
      <Button
        onClick={() =>
          updateStatus({ id: artwork.id, data: { status: ArtworkStatus.PENDING } })
        }
        variant="default"
      >
        <Send className="mr-2 h-4 w-4" />
        Request Publication
      </Button>
    )
  }

  // PENDING status
  if (artwork.status === ArtworkStatus.PENDING) {
    return (
      <div className="flex gap-2">
        {isArtist && (
          <Button
            variant="outline"
            onClick={() =>
              updateStatus({ id: artwork.id, data: { status: ArtworkStatus.DRAFT } })
            }
          >
            <XCircle className="mr-2 h-4 w-4" />
            Cancel Request
          </Button>
        )}
        {isManager && (
          <div className="flex items-center gap-2">
            <Button onClick={() => publish(artwork.id)}>
              <CheckCircle className="mr-2 h-4 w-4" />
              Approve & Publish
            </Button>
            {publishTask && (
              <Button
                variant="outline"
                size="icon"
                onClick={handleOpenTask}
                aria-label="View publish task"
              >
                <ExternalLink className="h-4 w-4" />
              </Button>
            )}
          </div>
        )}
      </div>
    )
  }

  // PUBLISHED status
  if (artwork.status === ArtworkStatus.PUBLISHED) {
    return (
      <div className="flex items-center gap-2">
        <Button variant="destructive" onClick={() => unpublish(artwork.id)}>
          <XCircle className="mr-2 h-4 w-4" />
          Unpublish
        </Button>
        {isManager && publishTask && (
          <Button
            variant="outline"
            size="icon"
            onClick={handleOpenTask}
            aria-label="View publish task"
          >
            <ExternalLink className="h-4 w-4" />
          </Button>
        )}
      </div>
    )
  }

  return null
}

